import { computed, ref, onBeforeUnmount } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { loadScript } from 'dashboard/helper/DOMHelpers';
import { TOLA_READY } from 'widget/constants/sdkEvents';

/**
 * Tola's own web widget, loaded on demand for "Chat with support".
 *
 * The token is the CHATWOOT_INBOX_TOKEN installation config (exposed as
 * `globalConfig.chatwootInboxToken`); `isAvailable` is false without it. The
 * SDK is injected on first toggle with the floating bubble hidden - the
 * calling control is the widget's only launcher - and the panel opens once
 * the SDK announces ready.
 */
export function useSupportChatWidget() {
  const globalConfig = useMapGetter('globalConfig/get');
  const currentUser = useMapGetter('getCurrentUser');

  const websiteToken = computed(() => globalConfig.value.chatwootInboxToken);
  const isAvailable = computed(() => Boolean(websiteToken.value));
  const isLoading = ref(false);

  // The widget contact is the logged-in agent. `hmac_identifier` is only
  // present when CHATWOOT_INBOX_HMAC_KEY is configured; the SDK ignores it
  // otherwise.
  const identifyUser = () => {
    const user = currentUser.value;
    if (!user?.email) return;
    window.$tola.setUser(user.email, {
      avatar_url: user.avatar_url,
      email: user.email,
      identifier_hash: user.hmac_identifier,
      name: user.name,
    });
  };

  const onWidgetReady = () => {
    isLoading.value = false;
    identifyUser();
    window.$tola.toggle('open');
  };

  const toggleSupportChat = async () => {
    if (window.$tola) {
      window.$tola.toggle();
      return;
    }
    if (isLoading.value) return;
    isLoading.value = true;
    window.tolaSettings = { hideMessageBubble: true, darkMode: 'auto' };
    window.addEventListener(TOLA_READY, onWidgetReady, { once: true });
    try {
      await loadScript('/packs/js/sdk.js', { id: 'tola-support-widget-sdk' });
      window.tolaSDK.run({
        websiteToken: websiteToken.value,
        baseUrl: window.location.origin,
      });
    } catch (error) {
      isLoading.value = false;
      window.removeEventListener(TOLA_READY, onWidgetReady);
    }
  };

  onBeforeUnmount(() => {
    window.removeEventListener(TOLA_READY, onWidgetReady);
  });

  return { isAvailable, isLoading, toggleSupportChat };
}
